import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { getStudents, createLesson } from '../../lib/api'
import { keys } from '../../lib/queryKeys'
import { PageHeader } from '../../components/PageHeader'
import { SearchSelectInput } from '../../components/SearchSelectInput'
import { m } from '../../paraglide/messages'
import type { Student } from '../../types'

export const Route = createFileRoute('/lessons/new')({
  component: NewLessonPage,
})

function NewLessonPage() {
  const navigate    = useNavigate()
  const queryClient = useQueryClient()

  const [studentId, setStudentId] = useState('')
  const [date, setDate]           = useState('')
  const [time, setTime]           = useState('')
  const [duration, setDuration]   = useState('60')
  const [error, setError]         = useState('')

  const { data: students, isLoading } = useQuery<Student[]>({
    queryKey: keys.students,
    queryFn: getStudents,
  })

  const mutation = useMutation({
    mutationFn: createLesson,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.lessons })
      navigate({ to: '/lessons' })
    },
    onError: (err) => setError((err as Error).message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!studentId || !date || !time) {
      setError(m.lessons_all_required())
      return
    }
    const scheduledAt = new Date(`${date}T${time}`).toISOString()
    mutation.mutate({
      studentId: Number(studentId),
      tutorId:   1,
      scheduledAt,
      durationMin: Number(duration),
    })
  }

  return (
    <div>
      <PageHeader title={m.lessons_new()} />

      {isLoading ? (
        <p className="text-zinc-400">{m.loading()}</p>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-md">
          <div className="flex flex-col gap-1">
            <label className="text-sm text-zinc-700">
              {m.form_student()}
            </label>
            <SearchSelectInput
              items={(students ?? []).map((s) => ({ id: s.id, name: s.name }))}
              selectedName={students?.find((s) => String(s.id) === studentId)?.name}
              onPick={(item) => setStudentId(String(item.id))}
              onClear={() => setStudentId('')}
              placeholder={m.lessons_select_student()}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm text-zinc-700" htmlFor="date">
              {m.form_date()}
            </label>
            <input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="border border-zinc-300 px-2 py-1.5 text-sm w-full focus:outline-none focus:border-zinc-900"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm text-zinc-700" htmlFor="time">
              {m.form_time()}
            </label>
            <input
              id="time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              required
              className="border border-zinc-300 px-2 py-1.5 text-sm w-full focus:outline-none focus:border-zinc-900"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm text-zinc-700" htmlFor="duration">
              {m.form_duration()}
            </label>
            <input
              id="duration"
              type="number"
              min="15"
              step="15"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              required
              className="border border-zinc-300 px-2 py-1.5 text-sm w-full focus:outline-none focus:border-zinc-900"
            />
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="bg-zinc-900 text-white px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {mutation.isPending ? m.lessons_scheduling() : m.lessons_schedule()}
            </button>
            <Link
              to="/lessons"
              className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
            >
              {m.action_cancel()}
            </Link>
          </div>
        </form>
      )}
    </div>
  )
}
